<script setup lang="ts">
import Button from '../../components/design-system/button.vue';
import { useAuthStore } from '~/stores/auth.store';
import { useSiteStore } from '~/stores/site.store';
import UserBalances from '~/components/user/user-balances.vue';
import GenericThemeToggle from '../user-interface/generic-theme-toggle.vue';
import ExternalIcon from '../design-system/icons/ExternalIcon.vue';
import ChatIcon from '../design-system/icons/ChatIcon.vue';
import { loginRoutes, registerRoutes } from 'assets/data/config.js';
import { useNotifications } from '~/composables/useStates/useNotifications';
import type { IHamburgerMenuState } from '~/interfaces/components/layout.ts';

const { $enabled, $toggleChat, $trackEvent } = useNuxtApp();

const route = useRoute();
const auth = useAuthStore();

const settings = useSiteStore();

const { panelOpen, notificationsState } = useNotifications();

const loginButtonType = computed(() =>
  registerRoutes.includes(route.path) ? 'primary' : 'tertiary-alternative',
);

const loginHandler = () => {
  if (registerRoutes.includes(route.path)) {
    navigateTo('/login');
  } else {
    settings.activateModal('login');
  }
};

const registerHandler = () => {
  if (loginRoutes.includes(route.path)) {
    navigateTo('/register');
  } else {
    settings.activateModal('register');
  }
};

const hamburgerMenuState = inject<IHamburgerMenuState>('hamburgerMenuState');
</script>
<template>
  <div>
    <div class="flex items-center justify-items-end gap-2">
      <div class="hidden md:block">
        <div
          element-name="nav-bar-search"
          class="flex items-center cursor-pointer p-2 rounded-xl bg-primary text-base text-base-color whitespace-nowrap h-10"
          @click="settings.activateModal('search')"
        >
          <span class="hidden lg:block">{{ $t('search-games') }}</span>
          <ExternalIcon
            class="lg:ml-2 w-6 mx-auto"
            icon-name="search-magnifying"
          />
        </div>
      </div>
      <!--    Logged out -->
      <div v-if="!auth.loggedIn" class="account flex items-center h-full gap-2">
        <Button
          v-if="!loginRoutes.includes(route.path)"
          :aria-label="$t('login')"
          element-name="nav-bar-login"
          :type="loginButtonType"
          padding="wide"
          @click="loginHandler"
        >
          {{ $t('login') }}
        </Button>
        <Button
          v-if="!registerRoutes.includes(route.path)"
          :aria-label="$t('register')"
          element-name="nav-bar-register"
          group="primary"
          padding="wide"
          @click="registerHandler"
        >
          {{ $t('jpc-register') }}
        </Button>
      </div>
      <!--    Logged in-->
      <div v-else class="account flex items-center h-full gap-1">
        <UserBalances
          :display-cash-balance="!route.params?.game"
          display-account-balance-toggle
          deposit-button-style="full"
          deposit-button-fallback="text"
          display-restricted-tag
          display-deposit-button
          force-hide-complete-account
          rounding="xl"
        />
        <Button
          v-if="$enabled('website.notificationpanel')"
          :aria-label="$t('notification-panel')"
          element-name="display-notifications"
          class="hidden md:flex relative"
          type="tertiary-alternative"
          @click="[
            settings.toggleFeature({ feature: 'displayNotifications' }),
            $trackEvent('Notification', 'NOTIFICATION_OPEN_PANEL'),
            (panelOpen = true),
          ]"
        >
          <ExternalIcon class="w-6 mx-auto" icon-name="notification" />
          <div
            v-if="
              !!notificationsState?.allNotifications?.unread ||
              settings.getUnreadNotificationsCount > 0
            "
            class="red-badge animate-ping text-xxs"
          ></div>
          <div
            v-if="
              !!notificationsState?.allNotifications?.unread ||
              settings.getUnreadNotificationsCount > 0
            "
            class="red-badge text-xxs"
          >
            {{
              notificationsState?.allNotifications?.unread ||
              settings.getUnreadNotificationsCount
            }}
          </div>
        </Button>
        <Button
          :aria-label="$t('account')"
          class="hidden md:flex relative"
          type="tertiary-alternative"
          @click="hamburgerMenuState.active = true"
        >
          <ExternalIcon class="w-6 mx-auto" icon-name="account-settings" />
          <div
            v-if="!auth.getIsUserCompliant"
            class="red-badge animate-pulse"
          />
        </Button>
      </div>

      <Button
        v-if="$enabled('website.livechat')"
        :aria-label="$t('live-chat')"
        element-name="live-chat"
        class="hidden md:flex"
        type="tertiary-alternative"
        @click="$toggleChat()"
      >
        <ChatIcon />
      </Button>
      <GenericThemeToggle v-if="!auth.loggedIn" />
    </div>
  </div>
</template>
<style scoped></style>
